import get from 'lodash/get'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { Image, P, Wrapper } from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'

import ENV from 'config/environment'
import AIGeneratedNotesSummary from 'components/activity/ai-summary'
import { report8x8FetchError } from 'components/activity/helpers'
import EightXEightWarning from 'components/activity/eight-x-eight-warning'
import AircallEvent from 'components/aircall-event'
import EightxEightEvent from 'components/eight-x-eight-event'
import LeadEventItem from 'components/lead-event'
import { AircallEventAttributes } from 'lib/models/aircall-event'
import { EightxEightEventAttributes } from 'lib/models/eight-x-eight-event'
import { EightXEightEventsState } from 'state/reducers/eight-x-eight-events'
import { Event } from 'lib/models/event'
import { LeadEvent } from 'lib/models/lead-event'
import { Lead } from 'lib/models/lead'
import {
  fetchLeadEvents,
  fetchContactEvents,
  fetchAircallEventsForCustomer,
  fetchEightXEightEventsForCustomer,
} from 'state/actions'

const LeadEventWrapper = styled(Wrapper)`
  background: ${COLOR.WHITE};
  border: 1px solid ${COLOR.GREY.LIGHT};
  margin-bottom: ${GTR.M};
  padding: ${GTR.S};
  width: 100%;
`

const LoadingWrapper = styled(LeadEventWrapper)`
  padding: ${GTR.L};
`

/** Either a leadId or contactId will be passed to this component, which is the History tab for both Lead and Contact pages */
const LeadEventHistory = ({
  params,
  lead,
}: {
  params: { leadId?: string; contactId?: string }
  lead?: Lead
}): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingEightXEight, setIsLoadingEightXEight] = useState(true)
  const [eightXEightFailed, setEightXEightFailed] = useState(false)

  const leadEvents = useSelector<{ leadEvents: LeadEvent[] }>(
    (state) => state.leadEvents
  )
  const contactEvents = useSelector<{ contactEvents: Event[] }>(
    (state) => state.contactEvents
  )
  const aircallEvents = useSelector<{
    aircallEvents: AircallEventAttributes[]
  }>((state) => state.aircallEvents)
  const eightXEightEvents = useSelector<{
    eightXEightEvents: EightXEightEventsState
  }>((state) => state.eightXEightEvents.events)
  const dispatch = useDispatch()
  const { leadId, contactId } = params
  // Only the latest 8x8 request may change the loading and warning state, so
  // a request for a previous lead that finishes late is ignored
  const latest8x8Request = useRef(0)

  const load8x8EventsForCustomer = useCallback(async () => {
    latest8x8Request.current += 1
    const request = latest8x8Request.current
    setIsLoadingEightXEight(true)
    setEightXEightFailed(false)

    const eightXEightStartedAt = Date.now()
    const eightXEightResponse = await (
      dispatch(
        fetchEightXEightEventsForCustomer({
          contactId: contactId ? Number(contactId) : null,
          leadId: leadId ? Number(leadId) : null,
        })
      ) as unknown as Promise<{
        // redux-promise-middleware wraps the resolved axios response as
        // `{ value, action }` rather than returning it directly.
        value: { data: { meta?: { eightXEightFailed?: boolean } } }
      }>
    ).catch((error: Error) => {
      report8x8FetchError(error, {
        leadId: leadId ? Number(leadId) : null,
        contactId: contactId ? Number(contactId) : null,
        startedAt: eightXEightStartedAt,
      })
      return null
    })

    if (request !== latest8x8Request.current) return

    setEightXEightFailed(
      !eightXEightResponse ||
        Boolean(get(eightXEightResponse, 'value.data.meta.eightXEightFailed'))
    )
    setIsLoadingEightXEight(false)
  }, [dispatch, leadId, contactId])

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true)

      // 8x8 is often slow and sometimes hangs, so its calls load separately
      // and are added to the list when they arrive rather than holding up
      // everything else
      load8x8EventsForCustomer()

      // Each fetch is settled independently so a single source failing
      // doesn't leave the whole tab stuck loading and hide events from every
      // other source.
      const fetchPromises = [
        // fetchLeadEvents can take either leadId or contactId
        dispatch(
          fetchLeadEvents({
            params: {
              ...(leadId && { leadId }),
              ...(contactId && { contactId }),
            },
          })
        ),
        dispatch(
          fetchAircallEventsForCustomer({
            contactId: contactId ? Number(contactId) : null,
            leadId: leadId ? Number(leadId) : null,
          })
        ),
      ]

      // fetchContactEvents can only take contactId
      if (contactId)
        fetchPromises.push(
          dispatch(fetchContactEvents({ params: { contactId } }))
        )

      await Promise.allSettled(fetchPromises)
      setIsLoading(false)
    }
    fetchEvents()
  }, [dispatch, leadId, contactId, load8x8EventsForCustomer])

  if (isLoading) {
    return (
      <LoadingWrapper centered>
        <p>Loading...</p>
      </LoadingWrapper>
    )
  }

  // leadEvents use attributes.happenedAt, contactEvents use attributes.updatedAt,
  // aircallEvents use started_at * 1000, eightXEightEvents use createdAt
  const allEvents = [
    ...(leadEvents as LeadEvent[]),
    ...(contactEvents as Event[]),
    ...(aircallEvents as AircallEventAttributes[]),
    ...(eightXEightEvents as EightxEightEventAttributes[]),
  ].sort((a, b) => {
    let dateA: number | string = ''
    let dateB: number | string = ''

    if ((a as LeadEvent)?.attributes?.happenedAt) {
      dateA = (a as LeadEvent).attributes.happenedAt
    } else if ((a as Event)?.attributes?.updatedAt) {
      dateA = (a as Event).attributes.updatedAt
    } else if ((a as AircallEventAttributes)?.started_at) {
      dateA = (a as AircallEventAttributes).started_at * 1000
    } else if ((a as EightxEightEventAttributes)?.createdAt) {
      dateA = (a as EightxEightEventAttributes).createdAt
    }

    if ((b as LeadEvent)?.attributes?.happenedAt) {
      dateB = (b as LeadEvent).attributes.happenedAt
    } else if ((b as Event)?.attributes?.updatedAt) {
      dateB = (b as Event).attributes.updatedAt
    } else if ((b as AircallEventAttributes)?.started_at) {
      dateB = (b as AircallEventAttributes).started_at * 1000
    } else if ((b as EightxEightEventAttributes)?.createdAt) {
      dateB = (b as EightxEightEventAttributes).createdAt
    }

    return new Date(dateB).getTime() - new Date(dateA).getTime()
  })

  const eightXEightStatus = isLoadingEightXEight ? (
    <P size="S" color={COLOR.GREY.MEDIUM}>
      Loading 8x8 calls…
    </P>
  ) : (
    eightXEightFailed && (
      <EightXEightWarning onRetry={load8x8EventsForCustomer} />
    )
  )

  if (allEvents.length === 0) {
    if (isLoadingEightXEight) return <>{eightXEightStatus}</>

    return (
      <>
        {eightXEightFailed && (
          <EightXEightWarning onRetry={load8x8EventsForCustomer} />
        )}
        <Wrapper centered>
          <Image path="illustrations/cropped/tortoise" width={80} />{' '}
          <P>No events recorded yet</P>
        </Wrapper>
      </>
    )
  }

  return (
    <>
      {eightXEightStatus}
      <Wrapper separator>
        {ENV.FF_AI_NOTES_ENABLED && lead && (
          <Wrapper margin={[0, 0, 'L']}>
            <AIGeneratedNotesSummary
              lead={lead}
              leadEvents={leadEvents as LeadEvent[]}
            />
          </Wrapper>
        )}
        {allEvents.map((event) => {
          if ('attributes' in event) {
            return <LeadEventItem event={event as LeadEvent} key={event.id} />
          }
          if ('guid' in event) {
            // eightXEightEvents have a guid field; aircallEvents do not
            return (
              <EightxEightEvent
                event={event as EightxEightEventAttributes}
                key={event.id}
                leadId={params.leadId ? Number(params.leadId) : undefined}
              />
            )
          }
          return (
            <AircallEvent
              event={event as AircallEventAttributes}
              key={event.id}
              leadId={Number(params.leadId)}
            />
          )
        })}
      </Wrapper>
    </>
  )
}

export default LeadEventHistory
